import { ArrowDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import type { BoxProps } from "@highoutput/ui-components";
import {
  Box,
  Button,
  Center,
  Flex,
  GridItem,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
} from "@highoutput/ui-components";
import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import type { PostFiltersInput, PostsQuery } from "~/__generated__/graphql";
import { getSdk } from "~/__generated__/graphql";
import client from "~/config/client";
import useNotify from "~/hooks/use-notify";
import arrayUnique from "~/utils/array-unique";
import For from "../utilities/for";
import Show from "../utilities/show";
import Empty from "./empty";
import Post from "./post";

type PostListProps = {
  data: PostsQuery["posts"];
  whitelist: string[];
} & Omit<BoxProps, "children">;

export default function PostList({ data, whitelist, ...props }: PostListProps) {
  const notify = useNotify();

  const [posts, setPosts] = useState<typeof data>();
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);

  const fetchMore = useCallback(async () => {
    setFetchingMore(true);

    try {
      const response = await getSdk(client).Posts({
        pagination: {
          start: posts?.data.length ?? 0 + 1,
          limit: posts?.meta.pagination.pageSize,
        },
        sort: "publishedAt:desc",
        filters: { ...createFilter(search), id: { not: { in: whitelist } } },
      });

      setPosts((current) => {
        const merged = [...(current?.data ?? []), ...(response.posts?.data ?? [])];

        return {
          data: search
            ? merged
            : /* this ensures items are unique even if page is stale */
              arrayUnique(merged, (post) => post.id),
          meta: response.posts?.meta ?? {
            pagination: {
              page: 1,
              pageSize: 0,
              pageCount: 0,
              total: 0,
            },
          },
        };
      });
    } catch {
      notify("Unable to fetch more posts");
    } finally {
      setFetchingMore(false);
    }
  }, [notify, posts?.data.length, posts?.meta.pagination.pageSize, search, whitelist]);

  const handleSearch = async (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();

    setSearching(true);

    try {
      const response = await getSdk(client).Posts({
        pagination: {
          start: 0,
          limit: posts?.meta.pagination.pageSize,
        },
        sort: "publishedAt:desc",
        filters: { ...createFilter(search), id: { not: { in: whitelist } } },
      });

      setPosts(response.posts);
    } catch {
      notify("Unable to search");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    setPosts(data);
  }, [data]);

  useEffect(() => {
    return () => {
      setPosts(null);
      setSearch("");
      setSearching(false);
      setFetchingMore(false);
    };
  }, []);

  return (
    <Box {...props}>
      {/* <!-- Revamped Searchbox --> */}
      <Flex
        as="form"
        position="relative"
        paddingY={{
          base: "64px",
          lg: "80px",
        }}
        alignItems="center"
        justifyContent="center"
        mx="auto"
        height="calc(72px + 40px + 40px)"
        onSubmitCapture={handleSearch}
        autoComplete="off"
      >
        <Box left={0} top={0} position="absolute" bgColor="white" width="full" height="50%" />

        <InputGroup
          size="lg"
          maxWidth={{
            base: "calc(229px + 114px)",
            md: "475px" /* added to cover md breakpoint 🚀 */,
            lg: "calc(613px + 115px)",
          }}
          height={{
            base: "48px",
            lg: "72px",
          }}
          bgColor="white"
          boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
          rounded="8px"
        >
          <InputLeftElement
            pointerEvents="none"
            height="full"
            paddingLeft="24px"
            paddingRight="8px"
          >
            <Icon as={MagnifyingGlassIcon} width="20px" height="20px" stroke="rgba(13,48,80,0.8)" />
          </InputLeftElement>

          <Input
            name="search"
            width="full"
            height="full"
            placeholder="Search"
            onChange={(e) => setSearch(e.target.value)}
            borderRight="0px"
            borderColor="rgba(240,237,236,0.4)"
            rounded="8px"
            _hover={{
              borderColor: "rgba(240,237,236,0.4)",
            }}
            fontSize="18px"
            lineHeight="18px"
            autoComplete="off"
          />

          <InputRightElement
            as={Button}
            variant="unstyled"
            aria-label="Search"
            pointerEvents="all"
            type="submit"
            height="full"
            width="115px"
            paddingX="24px"
            bgColor="primary.light"
            fontSize={{
              base: "20px",
              lg: "18px",
            }}
            lineHeight={{
              base: "20px",
              lg: "24px",
            }}
            roundedLeft="0"
            roundedRight="8px"
            isLoading={!!searching}
            isDisabled={!!searching}
            _hover={{
              bgColor: "primary.default",
            }}
            _focus={{
              bgColor: "primary.default",
            }}
            _disabled={{
              opacity: "0.75",
              bgColor: "primary.light",
              _hover: {
                opacity: "0.75",
                bgColor: "primary.light",
              },
            }}
          >
            Search
          </InputRightElement>
        </InputGroup>
      </Flex>
      <Box width="full">
        <Box
          marginX="auto"
          paddingX={{
            base: "16px",
            lg: "24px",
            xl: "160px",
          }}
          maxWidth={"1440px"}
        >
          <Show when={!posts?.data.length}>
            <Empty />
          </Show>

          <SimpleGrid
            columnGap="32px"
            rowGap={{
              base: "64px",
              lg: "48px",
            }}
            columns={{
              lg: 2,
            }}
          >
            <For each={posts?.data ?? []} getKey={(context, index) => context.id ?? index}>
              {(row) => {
                // const shouldExtendCol = !isEven(posts?.data.length ?? 0) && (i + 1) % 3 === 0;
                // {...(!isEven(posts?.data.length ?? 0) &&
                //   i === (posts?.data.length ? posts?.data.length - 1 : 0) && {
                //     colSpan: { lg: 2 },
                //   })}
                return (
                  <GridItem>
                    <Post data={row} />
                  </GridItem>
                );
              }}
            </For>
          </SimpleGrid>

          <Show when={!!posts && posts.meta.pagination.total > posts.data.length}>
            <Center
              marginTop={{
                base: "40px",
                lg: "80px",
                xl: "64px",
              }}
            >
              <Button
                leftIcon={<Icon as={ArrowDownIcon} width="20px" height="20px" />}
                height="44px"
                color="Gray.700"
                fontSize="20px"
                fontWeight="normal"
                variant="outline.default"
                rounded="8px"
                onClick={fetchMore}
                isLoading={fetchingMore}
                disabled={fetchingMore || /* disables button when searching */ searching}
              >
                Load more
              </Button>
            </Center>
          </Show>
        </Box>
      </Box>
    </Box>
  );
}

function createFilter(keyword?: string): PostFiltersInput {
  return {
    or: [
      {
        title: {
          containsi: keyword,
        },
      },
      {
        description: {
          containsi: keyword,
        },
      },
      {
        author: {
          name: {
            containsi: keyword,
          },
        },
      },
    ],
  };
}

// function isEven(n: number) {
//   return n % 2 === 0;
// }
